import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RBACProvider } from './contexts/RBACContext';
import { LocationProvider } from './contexts/LocationContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { ReminderProvider } from './contexts/ReminderContext';

// Auth Components
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import VerifyEmail from './components/Auth/VerifyEmail';
import Dashboard from './components/Dashboard/Dashboard';
import ProtectedRoute from './components/Common/ProtectedRoute';
import LoadingSpinner from './components/Common/LoadingSpinner';
import MainLayout from './components/Layout/MainLayout';

// RBAC Components
import RoleManagement from './components/RBAC/Roles/RoleManagement';
import PermissionManagement from './components/RBAC/Permissions/PermissionManagement';
import UserManagement from './components/RBAC/Users/UserManagement';

// Lazy-loaded heavy modules
const FinanceModule = lazy(() => import('./components/Finance/FinanceModule'));
const HRMModule = lazy(() => import('./components/HRM/HRMModule'));
const HirePurchaseModule = lazy(() => import('./components/HirePurchase/HirePurchaseModule'));

// Location Management Components
import LocationsList from './components/Admin/Locations/LocationsList';
import LocationDetails from './components/Admin/Locations/LocationDetails';
import SignUpSettings from './components/Admin/SignUpSettings';
import UserPermissionsReport from './components/Admin/UserPermissionsReport';
import AccessHistoryViewer from './components/Admin/AccessHistoryViewer';
import HRMAuditLogsViewer from './components/HRM/Reports/AuditLogsViewer';
import FinanceAuditLogsViewer from './components/Finance/Reports/AuditLogsViewer';
import AdminAuditLogsViewer from './components/Admin/Reports/AuditLogsViewer';
// User locations management removed - now handled in User Management page
import UserProfile from './components/User/UserProfile';

// Inventory Components
import InventoryDashboard from './components/Inventory/Dashboard/InventoryDashboard';
import CategoryManagement from './components/Inventory/Categories/CategoryManagement';
import PurchaseOrderManagement from './components/Inventory/PurchaseOrders/PurchaseOrderManagement';
import PurchaseRequestList from './components/Inventory/PurchaseRequests/PurchaseRequestList';
import PurchaseRequestForm from './components/Inventory/PurchaseRequests/PurchaseRequestForm';
import GTRList from './components/Inventory/GoodsTransferRequests/GTRList';
import GTRForm from './components/Inventory/GoodsTransferRequests/GTRForm';
import GTRDetails from './components/Inventory/GoodsTransferRequests/GTRDetails';
import GoodsIssueNoteList from './components/Inventory/GoodsIssueNotes/GoodsIssueNoteList';
import MaterialRequestNoteList from './components/Inventory/MaterialRequestNotes/MaterialRequestNoteList';
import InventoryConsumptionList from './components/Inventory/Consumption/InventoryConsumptionList';
import TransferInList from './components/Inventory/TransferIn/TransferInList';
import GRNManagement from './components/Inventory/GRN/GRNManagement';
import SupplierPaymentManagement from './components/Inventory/SupplierPayments/SupplierPaymentManagement';
import SupplierPaymentView from './components/Inventory/SupplierPayments/SupplierPaymentView';
import SupplierReturnNoteManagement from './components/Inventory/SupplierReturnNotes/SupplierReturnNoteManagement';
import DebitNoteManagement from './components/Inventory/DebitNotes/DebitNoteManagement';
import ItemManagement from './components/Inventory/Items/ItemManagement';
import RackManagement from './components/Inventory/Racks/RackManagement';
import StockManagement from './components/Inventory/Stock/StockManagement';
import StockOverview from './components/Inventory/Stock/StockOverview';
import SupplierManagement from './components/Inventory/Suppliers/SupplierManagement';
import PricingList from './components/Inventory/Pricing/PricingList';
import TaxList from './components/Inventory/Taxes/TaxList';
import SalesChannelList from './components/Inventory/SalesChannels/SalesChannelList';
import RouteList from './components/Inventory/Routes/RouteList';
import ReturnTypesList from './components/Inventory/ReturnTypes/ReturnTypesList';
import SalesRefList from './components/Inventory/SalesRefs/SalesRefList';
import POApprovalLevels from './components/Inventory/Settings/POApprovalLevels';
import BincardReport from './components/Inventory/Reports/BincardReport';
import StockLevelsReport from './components/Inventory/Reports/StockLevelsReport';
import StockMovementsReport from './components/Inventory/Reports/StockMovementsReport';
import StockAnalyticsReport from './components/Inventory/Reports/StockAnalyticsReport';
import StockValuationReport from './components/Inventory/Reports/StockValuationReport';
import StockAgingReport from './components/Inventory/Reports/StockAgingReport';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// App Layout Component
const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {children}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

// Main App Component
const AppContent = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <AppLayout>
        <LoadingSpinner />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin/users" element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports" element={
          <ProtectedRoute>
            <MainLayout>
              <UserPermissionsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/access-history" element={
          <ProtectedRoute>
            <MainLayout>
              <AccessHistoryViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/hrm" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS:READ">
            <MainLayout>
              <HRMAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/finance" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS:READ">
            <MainLayout>
              <FinanceAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/administration" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS:READ">
            <MainLayout>
              <AdminAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/roles" element={
          <ProtectedRoute>
            <MainLayout>
              <RoleManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/permissions" element={
          <ProtectedRoute>
            <MainLayout>
              <PermissionManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Location Management Routes */}
        <Route path="/admin/locations" element={
          <ProtectedRoute>
            <MainLayout>
              <LocationsList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/locations/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <LocationDetails />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/signup-settings" element={
          <ProtectedRoute>
            <MainLayout>
              <SignUpSettings />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* User Profile Routes */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <MainLayout>
              <UserProfile />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Finance Module Routes */}
        <Route path="/finance/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <FinanceModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Hire Purchase Module Routes */}
        <Route path="/hire-purchase/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <HirePurchaseModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* HRM Module Routes */}
        <Route path="/hrm/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <HRMModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Inventory Routes */}
        <Route path="/inventory/dashboard" element={
          <ProtectedRoute requiredPermission="INVENTORY:DASHBOARD:READ">
            <MainLayout>
              <InventoryDashboard />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/categories" element={
          <ProtectedRoute requiredPermission="INVENTORY:CATEGORIES:READ">
            <MainLayout>
              <CategoryManagement />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/purchase-orders" element={
          <ProtectedRoute>
            <MainLayout>
              <PurchaseOrderManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Purchase Request Routes */}
        <Route path="/inventory/purchase-requests" element={
          <ProtectedRoute requiredPermission="INVENTORY:PURCHASE_REQUESTS:READ">
            <MainLayout>
              <PurchaseRequestList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/purchase-requests/new" element={
          <ProtectedRoute requiredPermission="INVENTORY:PURCHASE_REQUESTS:CREATE">
            <MainLayout>
              <PurchaseRequestForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/purchase-requests/edit/:id" element={
          <ProtectedRoute requiredPermission="INVENTORY:PURCHASE_REQUESTS:UPDATE">
            <MainLayout>
              <PurchaseRequestForm />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Goods Transfer Request Routes */}
        <Route path="/inventory/goods-transfer-requests" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:READ">
            <MainLayout>
              <GTRList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/goods-transfer-requests/new" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:CREATE">
            <MainLayout>
              <GTRForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/goods-transfer-requests/edit/:id" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:UPDATE">
            <MainLayout>
              <GTRForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/goods-transfer-requests/view/:id" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:READ">
            <MainLayout>
              <GTRDetails />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Goods Issue Note Routes */}
        <Route path="/inventory/goods-issue-notes" element={
          <ProtectedRoute requiredPermission="INVENTORY:GOODS_ISSUE_NOTES:READ">
            <MainLayout>
              <GoodsIssueNoteList />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Material Request Note Routes */}
        <Route path="/inventory/material-request-notes" element={
          <ProtectedRoute requiredPermission="INVENTORY:MATERIAL_REQUEST_NOTES:READ">
            <MainLayout>
              <MaterialRequestNoteList />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Inventory Consumption Routes */}
        <Route path="/inventory/inventory-consumption" element={
          <ProtectedRoute requiredPermission="INVENTORY:INVENTORY_CONSUMPTION:READ">
            <MainLayout>
              <InventoryConsumptionList />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Transfer In Routes */}
        <Route path="/inventory/transfer-in" element={
          <ProtectedRoute requiredPermission="INVENTORY:TRANSFER_IN:READ">
            <MainLayout>
              <TransferInList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/grn" element={
          <ProtectedRoute>
            <MainLayout>
              <GRNManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Supplier Payment Routes */}
        <Route path="/inventory/supplier-payments" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierPaymentManagement />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/supplier-payments/view/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierPaymentView />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Supplier Return Notes Routes */}
        <Route path="/inventory/supplier-return-notes" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierReturnNoteManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Debit Notes Routes */}
        <Route path="/inventory/debit-notes" element={
          <ProtectedRoute>
            <MainLayout>
              <DebitNoteManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/items" element={
          <ProtectedRoute>
            <MainLayout>
              <ItemManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/racks" element={
          <ProtectedRoute>
            <MainLayout>
              <RackManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/stock" element={
          <ProtectedRoute>
            <MainLayout>
              <StockManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/stock-overview" element={
          <ProtectedRoute>
            <MainLayout>
              <StockOverview />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/suppliers" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/pricing" element={
          <ProtectedRoute>
            <MainLayout>
              <PricingList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/taxes" element={
          <ProtectedRoute>
            <MainLayout>
              <TaxList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/sales-channels" element={
          <ProtectedRoute>
            <MainLayout>
              <SalesChannelList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/distribution-routes" element={
          <ProtectedRoute>
            <MainLayout>
              <RouteList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/return-types" element={
          <ProtectedRoute>
            <MainLayout>
              <ReturnTypesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/sales-refs" element={
          <ProtectedRoute>
            <MainLayout>
              <SalesRefList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/settings/po-approval-levels" element={
          <ProtectedRoute>
            <MainLayout>
              <POApprovalLevels />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports" element={<Navigate to="/inventory/reports/bincard" replace />} />
        <Route path="/inventory/reports/stock-report" element={<Navigate to="/inventory/reports/stock-levels" replace />} />

        <Route path="/inventory/reports/bincard" element={
          <ProtectedRoute requiredPermission="INVENTORY:BINCARD:READ">
            <MainLayout>
              <BincardReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/stock-levels" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_LEVELS:READ">
            <MainLayout>
              <StockLevelsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/stock-movements" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_MOVEMENTS:READ">
            <MainLayout>
              <StockMovementsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/analytics" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_ANALYTICS:READ">
            <MainLayout>
              <StockAnalyticsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/valuation" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_VALUATION:READ">
            <MainLayout>
              <StockValuationReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/aging" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_AGING:READ">
            <MainLayout>
              <StockAgingReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppLayout>
  );
};

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-gray-900">404</h1>
      <p className="text-xl text-gray-600 mt-4">Page not found</p>
      <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-500 mt-4 inline-block">
        Go back to dashboard
      </Link>
    </div>
  </div>
);

// Main App Component with Providers
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AuthProvider>
          <LocationProvider>
            <ReminderProvider>
              <RBACProvider>
                <InventoryProvider>
                  <AppContent />
                </InventoryProvider>
              </RBACProvider>
            </ReminderProvider>
          </LocationProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
